import { Button, Input, Text } from "@assetwatch/ui";
import { useState } from "react";
import { categoryColorClass } from "./categoryColors";

type CategoryRowProps = {
  id: number;
  name: string;
  isGlobal: boolean;
  canManage: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSave: (id: number, name: string) => Promise<void>;
  onDelete: (id: number, name: string) => void;
};

export function CategoryRow({
  id,
  name,
  isGlobal,
  canManage,
  isSaving,
  isDeleting,
  onSave,
  onDelete,
}: CategoryRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(name);
  const canEdit = canManage && !isGlobal;

  async function save() {
    try {
      await onSave(id, editedName.trim());
      setIsEditing(false);
    } catch {
      // The mutation reports the failure to the user; keep the editor open.
    }
  }

  return (
    <li className="flex w-full items-center gap-(--spacing-12) border-b border-outline-subtle px-(--spacing-16) py-(--spacing-8)">
      <span
        aria-hidden="true"
        className={`size-(--spacing-12) shrink-0 rounded-full ${categoryColorClass(id)}`}
      />
      {isEditing ? (
        <div className="min-w-0 flex-1">
          <Input
            aria-label={`Category name for ${name}`}
            maxLength={100}
            onChange={(event) => setEditedName(event.target.value)}
            reserveSubtextSpace={false}
            size="sm"
            value={editedName}
          />
        </div>
      ) : (
        <div className="min-w-0 flex-1">
          <Text weight="medium" truncate>
            {name}
          </Text>
        </div>
      )}
      {isGlobal ? (
        <Text size="sm" tone="subtle">
          Global
        </Text>
      ) : null}
      {isEditing ? (
        <>
          <Button
            disabled={isSaving}
            onClick={() => {
              setEditedName(name);
              setIsEditing(false);
            }}
            size="sm"
            type="button"
            variant="ghost"
          >
            Cancel
          </Button>
          <Button
            disabled={!editedName.trim() || editedName.trim() === name}
            loading={isSaving}
            onClick={() => void save()}
            size="sm"
            type="button"
          >
            Save
          </Button>
        </>
      ) : (
        <>
          <Button
            aria-label={`Edit ${name}`}
            disabled={!canEdit || isSaving || isDeleting}
            onClick={() => {
              setEditedName(name);
              setIsEditing(true);
            }}
            size="sm"
            type="button"
            variant="ghost"
          >
            Edit
          </Button>
          <Button
            aria-label={`Delete ${name}`}
            disabled={!canEdit || isSaving || isDeleting}
            intent="destructive"
            onClick={() => onDelete(id, name)}
            size="sm"
            type="button"
            variant="ghost"
          >
            Delete
          </Button>
        </>
      )}
    </li>
  );
}
